import type { TMessage } from "@asurion-core/headless-chat-web";
import type { Logger } from "@expert/logging";
import type { SendExpertAssistMessage } from "@expert/shared-types";

import { getMessageDataType, MESSAGE_DATA_TYPES, useChatSessionStateStore } from "@asurion-core/headless-chat-web";
import { useMemo } from "react";

import type { CloseGuideSource, GuideSessionContext } from "../types";

import { GuideCard, GuideSelectionCard } from "../components";
import { getGuideCardPropsFromTextMessage, getPropsFromGuideSelectionMessage } from "./logicBuilderResolvers";

export interface GuidedFlowMessageMapperProps {
    latestMessage: TMessage | undefined;
    logger: Logger;
    onGuideClose: (source: CloseGuideSource) => void;
    onAskAssist?: () => void;
    opened: boolean;
    sendMessage: SendExpertAssistMessage;
    session: GuideSessionContext;
}

export const GuidedFlowMessageMapper = ({
    latestMessage,
    logger: loggerProp,
    onGuideClose,
    onAskAssist,
    opened,
    sendMessage,
    session,
}: GuidedFlowMessageMapperProps) => {
    const { sessionId } = session;
    const platformTicketId = useChatSessionStateStore((state) => state.platformTicketId);

    const logger = useMemo(
        () => loggerProp.child({ module: "GuidedFlowMessageMapper", platformTicketId }),
        [loggerProp, platformTicketId],
    );

    if (!latestMessage) return null;

    const messageType = getMessageDataType(latestMessage) as MESSAGE_DATA_TYPES;
    switch (messageType) {
        case MESSAGE_DATA_TYPES.CUSTOM: {
            const { currentStepId } = getPropsFromGuideSelectionMessage(latestMessage);
            return <GuideSelectionCard logger={logger} sessionId={sessionId} stepId={currentStepId} />;
        }
        case MESSAGE_DATA_TYPES.TEXT: {
            return (
                <GuideCard
                    logger={logger}
                    onGuideClose={onGuideClose}
                    onAskAssist={onAskAssist}
                    opened={opened}
                    sendMessage={sendMessage}
                    session={session}
                    step={getGuideCardPropsFromTextMessage(latestMessage)}
                />
            );
        }
        default:
            logger.warn({ latestMessage, messageType }, "Solve guide | Unsupported message type.");
            return null;
    }
};
